import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    HostBinding,
    HostListener,
    forwardRef,
    input,
    signal,
    computed,
    effect,
    untracked,
    viewChild,
    ElementRef,
    inject,
    DestroyRef,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import {
    MultiSelectComponent,
    DropDownSize,
    SummaryTagDirective,
    CustomMessagesComponent,
    HeaderTemplateDirective,
    ItemTemplateDirective,
    GroupTagTemplateDirective,
    NoDataTemplateDirective,
} from "@progress/kendo-angular-dropdowns";
import { WkTranslatePipe, NestedValuePipe } from "@shared/utils";
import { DropdownSearchService } from "../dropdown-search/dropdown-search.service";
import { DropdownSearchBoxComponent } from "../dropdown-search/dropdown-search-box.component";
import { DropdownPopupLayout, MULTISELECT_POPUP_LAYOUT } from "../dropdown-popup/dropdown-popup-layout.service";
import { ITranslateObject } from "../../../views-interfaces";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { CloseOnAncestorScrollDirective } from "../../directives/close-on-ancestor-scroll/index";
import { SuffixTemplateDirective } from "@progress/kendo-angular-common";
import { ButtonComponent } from "@progress/kendo-angular-buttons";
import { HighlightMatchPipe } from "../kendo-dropdownlist-wrapper/highlight-match.pipe";

@Component({
    selector: "kendo-multiselect-wrapper",
    templateUrl: "./kendo-multiselect-wrapper.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: "wk-msw cg3-host-multiselect",
        "cg3-theme": "PDS-v3",
    },
    providers: [
        WkTranslatePipe,
        NestedValuePipe,
        DropdownSearchService,
        DropdownPopupLayout,
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KendoMultiselectWrapper),
            multi: true,
        },
    ],
    imports: [
        JumpstartComponentsModule,
        MultiSelectComponent,
        SummaryTagDirective,
        CloseOnAncestorScrollDirective,
        CustomMessagesComponent,
        HeaderTemplateDirective,
        ItemTemplateDirective,
        GroupTagTemplateDirective,
        NoDataTemplateDirective,
        SuffixTemplateDirective,
        ButtonComponent,
        DropdownSearchBoxComponent,
        WkTranslatePipe,
        NestedValuePipe,
        HighlightMatchPipe,
    ],
})
class KendoMultiselectWrapper implements ControlValueAccessor {
    private readonly nestedValue = inject(NestedValuePipe);
    private readonly wkTranslate = inject(WkTranslatePipe);
    private readonly hostElement = inject(ElementRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdr = inject(ChangeDetectorRef);
    protected readonly search = inject(DropdownSearchService);
    private readonly popupLayout = inject(DropdownPopupLayout);

    private searchbarKeydownListener: ((event: KeyboardEvent) => void) | null = null;
    private searchbarBeforeInputListener: ((event: Event) => void) | null = null;
    private searchbarFocusListener: ((event: Event) => void) | null = null;
    private hostKeydownListener: ((event: KeyboardEvent) => void) | null = null;
    private hostFocusoutListener: ((event: FocusEvent) => void) | null = null;

    readonly textField = input("text");
    readonly valueField = input("value");
    readonly activeField = input("active");
    readonly size = input<DropDownSize>("medium");
    @Input() disabled = false;
    readonly placeholder = input("");
    readonly virtual = input(false);
    readonly valuePrimitive = input(true);
    readonly clearTitleText = input("");
    readonly label = input<string | ITranslateObject>("");

    // Search: `filterable` defaults true (the multiselect is searchable unless a consumer opts out).
    readonly filterable = input(true);

    // "text": the selected labels are written in the field. "count": the legacy "N selected" summary tag.
    readonly selectionDisplay = input<"text" | "count">("text");

    // Option list. null / undefined collapse to an empty array so every read downstream is safe.
    readonly data = input<any[], any[] | null | undefined>([], { transform: (value) => value ?? [] });

    // Open/focus/blur/click lifecycle state, kept off the Kendo instance.
    private focused = false;
    private blured = false;
    private clicked = false;
    private scrolled = false;
    private arrowDownKeyPress = false;

    private readonly multiselectRef = viewChild(MultiSelectComponent);
    private multiselect!: MultiSelectComponent;
    private readonly clearAllRef = viewChild<ElementRef<HTMLButtonElement>>("clearAllButton");
    private readonly searchBox = viewChild(DropdownSearchBoxComponent);

    // Set while the wrapper itself puts focus on the field, so the open popup doesn't pull it back.
    private focusingField = false;

    constructor() {
        this.destroyRef.onDestroy(() => {
            if (this.searchbarKeydownListener && this.multiselect?.searchbar?.input?.nativeElement) {
                this.multiselect.searchbar.input.nativeElement.removeEventListener("keydown", this.searchbarKeydownListener, true);
            }
            if (this.searchbarBeforeInputListener && this.multiselect?.searchbar?.input?.nativeElement) {
                this.multiselect.searchbar.input.nativeElement.removeEventListener("beforeinput", this.searchbarBeforeInputListener, true);
            }
            if (this.searchbarFocusListener && this.multiselect?.searchbar?.input?.nativeElement) {
                this.multiselect.searchbar.input.nativeElement.removeEventListener("focus", this.searchbarFocusListener, true);
            }
            if (this.hostKeydownListener && this.multiselect?.hostElement?.nativeElement) {
                this.multiselect.hostElement.nativeElement.removeEventListener("keydown", this.hostKeydownListener, true);
            }
            if (this.hostFocusoutListener && this.multiselect?.hostElement?.nativeElement) {
                this.multiselect.hostElement.nativeElement.removeEventListener("focusout", this.hostFocusoutListener, true);
            }
        });

        this.search.connect({
            data: this.data,
            textField: this.textField,
            filterable: this.filterable,
        });

        this.popupLayout.connect({
            layout: MULTISELECT_POPUP_LAYOUT,
            stops: {
                actions: () => [this.clearAllRef()?.nativeElement],
                search: () => this.searchBox()?.stops() ?? [],
            },
            field: () => this.multiselect?.searchbar?.input?.nativeElement,
        });

        // Wire up the inner multiselect once the view query resolves it (runs once).
        effect(() => {
            const component = this.multiselectRef();
            if (component && this.multiselect == null) {
                untracked(() => {
                    this.multiselect = component;
                    this.initializeMultiSelectComponent(component);
                });
            }
        });
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this.cdr.markForCheck();
    }

    protected handleValueChange(value: any): void {
        this.value = value;
        if (this.multiselect) this.multiselect.value = value === "" ? null : value;
        this.onChange(value);
        this.onTouched();
    }

    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    private readonly _value = signal<any>(null);
    // ControlValueAccessor requires this interface, so we can have the signal, as long as we write through the setter and read through the getter.
    @Input()
    set value(val: any) {
        this.writeValue(val);
    }
    get value() {
        return this._value();
    }

    writeValue(value: any): void {
        this._value.set(value || []);
        this.multiselect?.writeValue(value);
        this.cdr.markForCheck();
    }

    protected readonly selectedCount = computed(() => (Array.isArray(this._value()) ? this._value().length : 0));

    // Selected labels written in the field when `selectionDisplay` is "text".
    protected readonly selectedText = computed(() => {
        const value = this._value();
        if (!Array.isArray(value) || value.length === 0) return "";

        const items = this.data();
        const valueField = this.valueField();
        const textField = this.textField();

        return value
            .map((entry) => {
                const item = this.valuePrimitive() ? items.find((i) => this.nestedValue.transform(i, valueField) === entry) : entry;
                return item ? (this.wkTranslate.transform(this.nestedValue.transform(item, textField)) ?? "") : "";
            })
            .filter((label) => label !== "")
            .join("; ");
    });

    protected clearAll(): void {
        this.handleValueChange([]);

        const searchBox = this.searchBox();
        if (searchBox) searchBox.focus();
        else this.multiselect.searchbar.focus();
    }

    private initializeMultiSelectComponent(component: MultiSelectComponent): void {
        this.searchbarKeydownListener = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                if (this.multiselect.isOpen) this.multiselect.toggle(false);
                else this.hostElement.nativeElement.dispatchEvent(new KeyboardEvent("keydown", event));

                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
                return;
            }

            // Kendo only handles Enter while the popup is open; on the closed field it opens it.
            if (event.key === "Enter" && !this.multiselect.isOpen) {
                this.multiselect.toggle(true);
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }

            // The popup search box owns filtering; block character entry on the main field.
            if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
                event.preventDefault();
            }
        };
        this.multiselect.searchbar.input.nativeElement.addEventListener("keydown", this.searchbarKeydownListener, true);

        // Also blocks paste / IME text on the main field; the popup search box owns filtering.
        this.searchbarBeforeInputListener = (event: Event) => event.preventDefault();
        this.multiselect.searchbar.input.nativeElement.addEventListener("beforeinput", this.searchbarBeforeInputListener, true);

        // Keep focus in the popup search box: the main field must never hold focus while open.
        this.searchbarFocusListener = () => {
            if (this.multiselect.isOpen && !this.focusingField) this.searchBox()?.focus();
        };
        this.multiselect.searchbar.input.nativeElement.addEventListener("focus", this.searchbarFocusListener, true);

        this.hostKeydownListener = (event: KeyboardEvent) => {
            if (event.key === "Backspace") {
                const target = event.target as HTMLElement;
                const input = target as HTMLInputElement;
                const tagListElement = Array.from(target.parentElement?.children || []).find(
                    (child) => child.tagName === "KENDO-TAGLIST",
                ) as HTMLElement;
                if (
                    target.classList.contains("cg3-multiselect-body") &&
                    target.tagName === "INPUT" &&
                    input.value === "" &&
                    tagListElement &&
                    tagListElement?.children.length > 0
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                    event.stopImmediatePropagation();
                }
            }
        };
        this.multiselect.hostElement.nativeElement.addEventListener("keydown", this.hostKeydownListener, true);

        // Kendo treats any focusout as a blur unless the mouse is over the field; focus moves within the component aren't blurs.
        this.hostFocusoutListener = (event: FocusEvent) => {
            const host = this.multiselect.hostElement.nativeElement as HTMLElement;
            if (event.relatedTarget instanceof Node && host.contains(event.relatedTarget)) event.stopPropagation();
        };
        this.multiselect.hostElement.nativeElement.addEventListener("focusout", this.hostFocusoutListener, true);

        if (component.valueChange) {
            component.valueChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: any) => this.handleValueChange(value));
        }

        if (component.onFocus) {
            component.onFocus.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onMultiSelectFocus());
        }

        if (component.onBlur) {
            component.onBlur.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onMultiSelectBlur());
        }

        if (component.opened) {
            component.opened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onMultiSelectOpen(component));
        }

        if (component.close) {
            component.close.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: any) => this.onMultiSelectClose(event));
        }

        if (component.closed) {
            component.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
        }
    }

    protected onItemDisabled = (args: { dataItem: any; index: number }): boolean => {
        const item = args.dataItem;
        const keys = Object.keys(item);
        const activeField = this.activeField();
        if (!activeField || !keys.includes(activeField)) return false;

        return item[this.valueField()] !== this.value && !item[activeField];
    };

    protected toggleDropdown(): void {
        if (!this.multiselect) return;

        this.clicked = true;
        this.multiselect.toggle(!this.multiselect.isOpen);
        if (!this.multiselect.isOpen) this.multiselect.searchbar.focus();
        this.cdr.markForCheck();
    }

    private onMultiSelectBlur(): void {
        this.blured = true;
        this.multiselect.toggle(false);
        this.cdr.markForCheck();
    }

    private onMultiSelectFocus(): void {
        this.focused = true;
        this.blured = false;
        this.cdr.markForCheck();
    }

    private onMultiSelectOpen(select: MultiSelectComponent): void {
        this.clicked = false;
        select.focusItemAt(-1);
        this.arrowDownKeyPress = false;
        this.cdr.markForCheck();
        setTimeout(() => {
            const popup = select.popupRef?.popupElement;
            if (popup) this.popupLayout.attach(popup);
        }, 0);
    }

    private onMultiSelectClose(event: any): void {
        if (!this.scrolled && (!this.clicked || (this.focused && !this.blured))) event.preventDefault();
        this.clicked = false;
        this.scrolled = false;
        this.search.reset(); // immediate list reset + flush of any pending debounced term
        this.cdr.markForCheck();
    }

    @HostBinding("class.wk-msw--open")
    protected get isDropdownOpen() {
        return this.multiselect?.isOpen ?? false;
    }

    @HostBinding("class.wk-msw--text-selection")
    protected get isTextSelection() {
        return this.selectionDisplay() === "text";
    }

    @HostBinding("class.wk-msw--has-selection")
    protected get hasSelection() {
        return this.selectedCount() > 0;
    }

    // Kendo only navigates the list from keydowns on its own searchbar input, so the search box forwards them.
    protected onListNavigate(event: KeyboardEvent): void {
        this.multiselect?.handleNavigate(event);
    }

    // Tab order inside the open popup comes from the layout.
    @HostListener("keydown.tab", ["$event"])
    @HostListener("keydown.shift.tab", ["$event"])
    protected onTab(event: Event): void {
        if (this.multiselect?.isOpen && this.popupLayout.handleTab(event as KeyboardEvent)) this.closeToField();
    }

    @HostBinding("attr.size")
    protected get hostSize() {
        return this.size();
    }

    @HostListener("keydown.escape", ["$event"])
    @HostListener("document:keydown.escape", ["$event"])
    protected onCloseKey(event: Event): void {
        if (this.multiselect?.isOpen) {
            this.closeToField();
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        }
    }

    // Closing removes the popup; focus inside it goes back to the field rather than to the body.
    private closeToField(): void {
        const focusInPopup = this.multiselect.popupRef?.popupElement.contains(document.activeElement) ?? false;
        this.multiselect.toggle(false);
        // `toggle` emits no close event, so the search is reset here.
        this.search.reset();
        if (focusInPopup) {
            this.focusingField = true;
            this.multiselect.searchbar.focus();
            this.focusingField = false;
        }
    }

    // Hovering a row measures its label. When the label is clipped, mounts the tooltip targeting its id.
    // A row is an option in the popup, or the value area of the main input.
    private static readonly TRUNCATION_ROW_SELECTOR = "li.k-list-item, .k-input-values";
    private static readonly TRUNCATION_LABEL_SELECTOR = ".multiselect-wrapper-value, .multiselect-wrapper-item";

    // Per-instance prefix, so the label ids stay unique with several wrappers on a page.
    private static nextId = 0;
    private readonly idPrefix = `wk-msw-${KendoMultiselectWrapper.nextId++}`;

    // Stores ids of the labels measured as clipped: only those get a tooltip.
    private readonly clippedLabelIds = signal<ReadonlySet<string>>(new Set());

    protected readonly valueLabelId = `${this.idPrefix}-value`;

    // Option ids are handed out in render order and cached per data item: the item template exposes no
    // index, and the value field is neither guaranteed to be present nor unique.
    private readonly optionLabelIds = new Map<unknown, string>();

    protected optionLabelId(dataItem: unknown): string {
        let labelId = this.optionLabelIds.get(dataItem);
        if (!labelId) {
            labelId = `${this.idPrefix}-option-${this.optionLabelIds.size}`;
            this.optionLabelIds.set(dataItem, labelId);
        }

        return labelId;
    }

    protected isClipped(labelId: string): boolean {
        return this.clippedLabelIds().has(labelId);
    }

    // Measures the hovered row's label and stores its id if clipped/truncated
    protected onHover(event: Event): void {
        const row = (event.target as HTMLElement | null)?.closest<HTMLElement>(KendoMultiselectWrapper.TRUNCATION_ROW_SELECTOR);
        const label = row?.querySelector<HTMLElement>(KendoMultiselectWrapper.TRUNCATION_LABEL_SELECTOR);
        if (!label?.id) return;

        const clipped = label.scrollWidth > label.clientWidth || label.scrollHeight > label.clientHeight;
        this.clippedLabelIds.update((ids) => {
            if (ids.has(label.id) === clipped) return ids;

            const next = new Set(ids);
            if (clipped) next.add(label.id);
            else next.delete(label.id);

            return next;
        });
    }

    @HostListener("keydown.arrowdown")
    protected onArrowDown(): void {
        if (!this.multiselect) return;

        // Open the closed popup; `isOpen` is the boolean state - `open` is Kendo's event emitter.
        if (!this.multiselect.isOpen) {
            this.multiselect.toggle(true);
            return;
        }

        if (this.arrowDownKeyPress) return;

        this.multiselect.focusItemAt(0);
        this.arrowDownKeyPress = true;
    }
}

export { KendoMultiselectWrapper };
